import { addListener } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from 'services/store';
import {
  selectEffectsOrder,
  selectEffectsState,
} from 'services/store/remixer/selectors';
import { actions } from 'services/store/remixer';
import { type Effects } from 'services/store/remixer/types';
import { EFFECT_VALUE_DIVISOR } from 'pages/Remixer/constants';
import { type EffectsChain } from 'pages/Remixer/audioHelpers';
import {
  useCreateEffectsChain,
  useRemixerAudioContext,
} from 'pages/Remixer/hooks';
import useSourceNode from './useSourceNode';

const useEffectsChain = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { create } = useCreateEffectsChain();
  const [isReady, setIsReady] = useState(false);

  const { audioContext } = useRemixerAudioContext();
  const sourceNode = useSourceNode();

  const effectsState = useSelector(selectEffectsState);
  const effectsOrder = useSelector(selectEffectsOrder);

  const [chain, setChain] = useState<EffectsChain>();

  useEffect(() => {
    (async () => {
      if (!audioContext) {
        return;
      }
      const effectsChain = await create(effectsOrder, audioContext);
      setChain(effectsChain);
      Object.keys(effectsChain.nodes).forEach((effectName) => {
        const node = effectsChain.nodes[effectName as Effects];
        if (!node) {
          return;
        }
        const value = effectsState[effectName as Effects];
        node.control.value = value / EFFECT_VALUE_DIVISOR;
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectsOrder, audioContext]);

  useEffect(() => {
    if (!chain || !audioContext) {
      return;
    }
    const unsubscribe = dispatch(
      addListener({
        actionCreator: actions.setEffectValue,
        effect: ({ payload: { effect, value } }) => {
          const effectControlNode = chain.nodes[effect]?.control;
          effectControlNode?.setValueAtTime(
            value / EFFECT_VALUE_DIVISOR,
            audioContext.currentTime
          );
        },
      })
    );

    return () => {
      unsubscribe();
    };
  }, [chain, audioContext, dispatch]);

  useEffect(() => {
    const isChainReady = chain && audioContext && sourceNode;

    if (!isChainReady) {
      setIsReady(false);
      return;
    }

    sourceNode.connect(chain.input);
    chain.output.connect(audioContext.destination);

    setIsReady(true);

    return () => {
      chain.output.disconnect(audioContext.destination);
      sourceNode.disconnect(chain.input);
    };
  }, [audioContext, sourceNode, chain]);

  return { isReady };
};

export default useEffectsChain;
